/**
 * NoteList — the master rail: search, New note, Archived toggle, and the
 * reverse-chronological note list with a pinned group on top.
 * Presentation only; the parent (NotesModule) owns fetching + filtering.
 */
import { memo } from 'react';
import { formatRelativeTime } from '@/lib/timeUtils';
import { objectDragSource } from '@/common/dnd/objectDragSource';
import { displayTitle, snippetOf, type NoteObject } from '../notesShared';

interface NoteListProps {
  notes: NoteObject[];
  activeId: string | null;
  /** Live draft body for the active note, so its card title/snippet update as you type. */
  activeDraft?: string;
  onSelect: (id: string) => void;
  query: string;
  onQueryChange: (q: string) => void;
  showArchived: boolean;
  onToggleArchived: () => void;
  onNew: () => void;
  activeCount: number;
  archivedCount: number;
  isLoading: boolean;
}

function PinDot() {
  return (
    <span className="notes-pin-dot" aria-hidden="true">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14 4v5l3 3v2h-5v6l-1 1-1-1v-6H5v-2l3-3V4z" />
      </svg>
    </span>
  );
}

// Memoized so typing in the editor only re-renders the active card (whose
// `body` prop changes), not the whole list.
const NoteCard = memo(function NoteCard({
  note,
  body,
  active,
  onSelect,
}: {
  note: NoteObject;
  body: string;
  active: boolean;
  onSelect: (id: string) => void;
}) {
  const t = displayTitle({ body });
  const snip = snippetOf(body);
  return (
    <button
      type="button"
      className={`notes-card${active ? ' is-active' : ''}`}
      onClick={() => onSelect(note.id)}
      // Drag this note into the chat composer to @-reference it (issue #1457).
      {...objectDragSource({ id: note.id, type: 'note', title: t.text })}
    >
      <div className="notes-card-top">
        {note.pinned ? <PinDot /> : null}
        <span className={`notes-card-title${t.untitled ? ' is-untitled' : ''}`}>{t.text}</span>
      </div>
      <div className="notes-card-snip">{snip}</div>
      <div className="notes-card-time">{formatRelativeTime(note.updatedAt)}</div>
    </button>
  );
});

export default function NoteList({
  notes,
  activeId,
  activeDraft,
  onSelect,
  query,
  onQueryChange,
  showArchived,
  onToggleArchived,
  onNew,
  activeCount,
  archivedCount,
  isLoading,
}: NoteListProps) {
  const pinned = showArchived ? [] : notes.filter((n) => n.pinned);
  const rest = showArchived ? notes : notes.filter((n) => !n.pinned);

  // The active card shows the live draft; all others show their stored body.
  const bodyFor = (n: NoteObject) =>
    n.id === activeId && activeDraft !== undefined ? activeDraft : (n.body ?? '');

  const countLabel = showArchived
    ? `${archivedCount} archived`
    : `${activeCount} ${activeCount === 1 ? 'note' : 'notes'}`;

  const emptyText = query.trim()
    ? `No notes match “${query.trim()}”`
    : showArchived
      ? 'Nothing archived'
      : 'No notes yet';

  return (
    <aside className="notes-rail">
      <div className="notes-rail-head">
        <div className="notes-rail-count-row">
          <span className="notes-rail-count">{isLoading ? '—' : countLabel}</span>
        </div>
        <div className="notes-search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            type="text"
            value={query}
            placeholder="Search notes…"
            autoComplete="off"
            onChange={(e) => onQueryChange(e.target.value)}
          />
        </div>
        <div className="notes-rail-actions">
          <button type="button" className="module-toolbar-button is-primary" onClick={onNew} title="New note">
            + New
          </button>
          <button
            type="button"
            className="module-toolbar-button"
            onClick={onToggleArchived}
            aria-pressed={showArchived}
          >
            Archived
          </button>
        </div>
      </div>

      <div className="notes-list xv-scroll">
        {notes.length === 0 ? (
          <div className="notes-empty">{isLoading ? 'Loading…' : emptyText}</div>
        ) : (
          <>
            {pinned.length > 0 && (
              <>
                <div className="notes-group-label"><span>Pinned</span><span className="notes-group-line" /></div>
                {pinned.map((n) => (
                  <NoteCard key={n.id} note={n} body={bodyFor(n)} active={n.id === activeId} onSelect={onSelect} />
                ))}
                {rest.length > 0 && (
                  <div className="notes-group-label"><span>{query.trim() ? 'Other matches' : 'All notes'}</span><span className="notes-group-line" /></div>
                )}
              </>
            )}
            {rest.map((n) => (
              <NoteCard key={n.id} note={n} body={bodyFor(n)} active={n.id === activeId} onSelect={onSelect} />
            ))}
          </>
        )}
      </div>
    </aside>
  );
}
